import { useOne } from '@refinedev/core';
import { Skeleton, Typography } from 'antd';
import type { ContentProductDetailOut } from '@/api/generated/schemas';
import { ShowLink } from '@/components/common/ShowLink';

const RESOURCE = 'content_catalog.products';

interface ProductLazyCellProps {
  productId: number;
  /** Title already known by the caller, shown until the fetch lands. */
  fallbackTitle?: string | null;
}

/**
 * Title link for a content-catalog product referenced by id. Fetches
 * the product lazily; mirrors `PublicationLazyCell` minus the cover.
 */
export const ProductLazyCell = ({
  productId,
  fallbackTitle,
}: ProductLazyCellProps) => {
  const known = fallbackTitle?.trim() || undefined;

  const { result, query } = useOne<ContentProductDetailOut>({
    resource: RESOURCE,
    id: productId,
    queryOptions: { enabled: !known },
  });

  if (query.isLoading && !known) {
    return <Skeleton.Input active size="small" style={{ width: 160 }} />;
  }

  const title = known ?? result?.title ?? `#${productId}`;

  return (
    <ShowLink resource={RESOURCE} id={productId}>
      <Typography.Text
        style={{ color: 'inherit' }}
        ellipsis={{ tooltip: title }}
      >
        {title}
      </Typography.Text>
    </ShowLink>
  );
};
